import { useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { notify } from "@/lib/notify";

import { superAdminAuthApi } from "@/lib/api/superAdminAuth";
import { ApiRequestError } from "@/lib/api/client";
import { useAuthStore } from "@/stores/auth-store";
import { Button } from "@/components/ui/Button";
import { PasswordInput } from "@/components/ui/Input";
import { ErrorMessage } from "@/components/ui/ErrorMessage";
import { Icon } from "@/components/layout/Icon";
import { Tabs } from "@/components/ui/Tabs";
import { CredentialsLoginForm } from "./CredentialsLoginForm";

/**
 * The dedicated Super Admin login surface - "Login with Credentials |
 * Login with Key" (spec section 13). A new route rather than a change to
 * the shared /login page: that page is used by every tenant employee too,
 * and has no way to know a Super Admin is about to sign in before they do,
 * so a Super-Admin-only "Login with Key" control has no business appearing
 * there. /login itself keeps working unchanged - a Super Admin may still
 * sign in with credentials there exactly as before; this page adds a
 * second front door, not a replacement for the first.
 */
export function SuperAdminLoginPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<"credentials" | "key">("credentials");

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-brand px-4">
      <div className="w-full max-w-sm">
        <div className="mb-8 text-center">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-primary-600 text-white shadow-lg">
            <Icon name="shield" className="h-6 w-6" />
          </div>
          <h1 className="text-xl font-semibold text-white">Super Admin Login</h1>
          <p className="mt-1 text-sm text-primary-200">Platform administrator access</p>
        </div>

        <div className="rounded-2xl bg-white p-6 shadow-2xl">
          <Tabs
            tabs={[
              { key: "credentials", label: "Login with Credentials" },
              { key: "key", label: "Login with Key" },
            ]}
            active={tab}
            onChange={(k) => setTab(k as "credentials" | "key")}
          />
          <div className="mt-4">
            {tab === "credentials" ? (
              <CredentialsLoginForm onSuccess={() => navigate("/", { replace: true })} />
            ) : (
              <KeyLoginForm onSuccess={() => navigate("/", { replace: true })} onBack={() => setTab("credentials")} />
            )}
          </div>
        </div>

        <p className="mt-6 text-center text-xs text-primary-300">
          Trouble signing in? Contact another platform administrator.
        </p>
      </div>
    </div>
  );
}

function KeyLoginForm({ onSuccess, onBack }: { onSuccess: () => void; onBack: () => void }) {
  const setSession = useAuthStore((s) => s.setSession);
  const [needsMFACode, setNeedsMFACode] = useState(false);

  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm<{ key: string; mfa_code: string }>({ defaultValues: { key: "", mfa_code: "" } });

  const mutation = useMutation({
    // Same "this form shows its own inline error banner" opt-out as
    // CredentialsLoginForm - see that component's identical comment.
    meta: { silent: true },
    mutationFn: superAdminAuthApi.keyLogin,
    onSuccess: (result) => {
      setSession({
        accessToken: result.access_token,
        refreshToken: result.refresh_token,
        user: result.user,
        tenant: result.tenant,
      });
      notify.success(`Welcome back, ${result.user.first_name}!`);
      onSuccess();
    },
    onError: (error) => {
      // The backend deliberately returns the identical generic failure for
      // every rejection reason (unknown/expired/revoked/already-used) so a
      // caller can never enumerate keys - the ONE distinguishable case is
      // "this key requires a multi-factor authentication code," carried the
      // same way password login signals it (an mfa_code field error), which
      // is genuinely safe to reveal since it names no account.
      if (error instanceof ApiRequestError && error.appError.fields?.some((f) => f.field === "mfa_code")) {
        setNeedsMFACode(true);
      }
    },
  });

  return (
    <form onSubmit={handleSubmit((values) => mutation.mutate(values))} className="flex flex-col gap-4">
      <PasswordInput
        id="super-admin-access-key"
        label="Access Key"
        placeholder="sak_••••••••••••••••••••••••"
        autoComplete="off"
        error={errors.key?.message}
        {...register("key", { required: true })}
      />
      {needsMFACode && (
        <div className="flex items-start gap-2 rounded-lg bg-slate-50 p-3">
          <Icon name="lock" className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
          <div className="flex-1">
            <label htmlFor="key-mfa-code" className="mb-1 block text-sm font-medium text-slate-700">
              Authentication code
            </label>
            <input
              id="key-mfa-code"
              placeholder="123456"
              autoComplete="one-time-code"
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100"
              {...register("mfa_code")}
            />
            <p className="mt-1 text-xs text-slate-500">Enter the code from your authenticator app, or one of your backup codes.</p>
          </div>
        </div>
      )}

      {mutation.isError && (!needsMFACode || getValues("mfa_code")) && <ErrorMessage error={mutation.error} />}

      <Button type="submit" loading={mutation.isPending} fullWidth className="mt-1">
        <Icon name="key" className="mr-1.5 h-4 w-4" />
        Login with Key
      </Button>
      <button type="button" onClick={onBack} className="text-center text-sm text-primary-700 hover:underline">
        ← Back to Credentials
      </button>
    </form>
  );
}
